import { CHANNELS } from "../../shared/channels";
import { CHIX_INBOX, TASK_STATUSES, boardKey, taskKey } from "../../shared/task_status";
import { StudioJob, StudioState, TaskEntry, TaskSnapshot, TaskStatus } from "../../shared/types";
import { formatMoney, jobOwed, jobPrice } from "../../shared/money";
import { call, listen } from "../bridge";
import { byId, flashToast, renderErrors, renderList } from "../dom";
import { t } from "../i18n";
import { onViewShown } from "../navigation";
import { buzz, setPulsing } from "../tweens";
import { formatDate, formatTime, matchesSearch } from "../utils";
import { buildTaskItem, sortTasks } from "./task_item";
import { openCommission } from "./studio_view";
import { TASK_TO_COMM, buildCommTile, commSign, commTaskStatus, loadCommSign } from "../commission_card";
import { openTaskEditor } from "./task_editor";
import { Kanban, buildArmedTrash, createKanban, crunchCard } from "../kanban";
import { STATUS_LABEL, buildStatusChip, buildTrashButton, onTaskAction, openStatusPicker, setStatus, trashTask } from "./task_actions";
import { onSharedChange, readSharedAll } from "../shares";
import { canUse, myId } from "../me";
import { cloneTemplate, fillFields } from "../dom";
import { isChixBoard, renderTuner, setChixBoards, tunerBoards } from "./task_boards";

// full task list or board with search and a source filter. statuses live in chix only, per person

let snapshot: TaskSnapshot = { tasks: [], errors: [], updatedAt: "" };
let shared: Array<{ id: string; name: string; data: TaskSnapshot }> = [];
let mode: "list" | "board" = "list";
let showHidden = false;
// the board tuner: "all", a chix board id or "plane:<project>". saved per person, so every device opens on it
let pick = "all";
let pickTouched = false;

// my commissions ride along as cards of their own, when this person has the studio
let comms: StudioJob[] = [];
// ones just moved to done stay on the board even when settled, so they do not vanish under the finger
const justDone = new Set<string>();

type Entry = { kind: "task"; task: TaskEntry } | { kind: "comm"; job: StudioJob };
const entryKey = (entry: Entry): string => (entry.kind === "task" ? taskKey(entry.task) : `commission:${entry.job.id}`);

const statusOf = (task: TaskEntry): TaskStatus => snapshot.meta?.status[taskKey(task)] ?? "todo";

// a pick whose board is gone (a plane project with nothing open, a deleted board) shows everything
function currentPick(): string {
  return pick === "all" || tunerBoards(snapshot.tasks).some((board) => board.key === pick) ? pick : "all";
}

// shared lists are someone else's boards, so only my own list follows the tuner
function visibleTasks(tasks: TaskEntry[], byBoard = true): TaskEntry[] {
  const search = byId<HTMLInputElement>("tasks-search").value;
  const source = byId<HTMLSelectElement>("tasks-source").value;
  const board = byBoard ? currentPick() : "all";
  return sortTasks(tasks).filter((task) => (source === "all" || task.source === source) && (board === "all" || boardKey(task) === board) && matchesSearch(search, task.title, task.description, task.board));
}

// settled and done commissions are history, only the ones still owing, just finished or paid lately show
function visibleComms(): StudioJob[] {
  if (!canUse("studio", "edit")) return [];
  const search = byId<HTMLInputElement>("tasks-search").value;
  const source = byId<HTMLSelectElement>("tasks-source").value;
  if (source !== "all" && source !== "commission") return [];
  // commissions sit on no board, they ride along with "all"
  if (currentPick() !== "all") return [];
  const cutoff = new Date(Date.now() - 14 * 86_400_000).toISOString().slice(0, 10);
  return comms
    .filter((job) => job.status !== "done" || jobOwed(job) > 0 || justDone.has(job.id) || job.paidAt >= cutoff)
    .filter((job) => matchesSearch(search, job.name, job.client, job.description, job.typeName))
    .sort((a, b) => (a.due || "9999").localeCompare(b.due || "9999"));
}

function buildCommRow(job: StudioJob): HTMLElement {
  const status = commTaskStatus(job);
  const sign = commSign(comms);
  const price = jobPrice(job);
  const client = job.client && job.client.toLowerCase() !== job.name.toLowerCase() ? t("for {client}", { client: job.client }) : "";
  const money = price ? [formatMoney(price, sign), jobOwed(job) > 0 ? t("{owed} owed", { owed: formatMoney(jobOwed(job), sign) }) : t("paid")] : [job.amount];
  const meta = [client, ...money, job.status === "review" && t("review"), job.due && t("due {date}", { date: formatDate(job.due) })].filter(Boolean).join(" · ");
  const item = fillFields(cloneTemplate("template-task"), { source: t("commission"), title: job.name, meta });
  item.querySelector(".badge")?.classList.add("commission");
  item.classList.add("from-commission", `status-${status}`);
  item.addEventListener("click", () => openCommission(job.id));
  item.querySelector(".task-actions")?.before(buildStatusChip({ title: job.name }, status, (next) => void moveComm(job, next)));
  item.append(buildArmedTrash(t("delete commission"), true, () => void trashComm(job, item)));
  return item;
}

function render(): void {
  const visibleCards = visibleComms();
  const visible = visibleTasks(snapshot.tasks);
  const entries: Entry[] = [...visible.map((task): Entry => ({ kind: "task", task })), ...visibleCards.map((job): Entry => ({ kind: "comm", job }))];
  for (const option of byId<HTMLSelectElement>("tasks-source").options) if (option.value === "commission") option.hidden = !canUse("studio", "edit");

  for (const button of document.querySelectorAll<HTMLElement>("#tasks-mode [data-mode]")) {
    const on = button.dataset.mode === mode;
    button.classList.toggle("active", on);
    button.setAttribute("aria-pressed", String(on));
  }
  byId("tasks-list").hidden = mode !== "list";
  byId("tasks-board").hidden = mode !== "board";

  if (mode === "list") renderList(byId("tasks-list"), entries, (entry) => (entry.kind === "task" ? buildTaskItem(entry.task, false, statusOf(entry.task), true) : buildCommRow(entry.job)));
  else renderBoard(entries);

  renderBoards();
  renderHidden();
  renderErrors(byId("tasks-errors"), snapshot.errors);
  byId("tasks-updated").textContent = t("{count} shown, updated {time}", { count: entries.length, time: formatTime(snapshot.updatedAt) });
  const theirs = shared.map((person) => ({ ...person, data: visibleTasks(person.data.tasks, false) }));
  renderSharedFold(theirs);
}

// ---------- board tuner ----------

function renderBoards(): void {
  const open = snapshot.tasks.filter((task) => statusOf(task) !== "done");
  renderTuner({
    host: byId("tasks-boards"),
    tasks: snapshot.tasks,
    pick: currentPick(),
    total: open.length,
    open: (key) => open.filter((task) => boardKey(task) === key).length,
    choose: choosePick,
    // adding a board picks it, deleting the picked one falls back to the inbox
    changed: (fresh) => {
      if (fresh.meta?.pick) pick = fresh.meta.pick;
      apply(fresh);
    }
  });
}

function choosePick(next: string): void {
  if (next === pick) return;
  pick = next;
  pickTouched = true;
  buzz(6);
  render();
  void call(CHANNELS.tasksPick, next).catch(() => undefined);
}

// ---------- shared with me: one closed button, then a read only list per person ----------

// open or closed is remembered per person on this device
const flagKey = (name: string) => `chix-tasks-${name}-${myId() || "me"}`;
function readFlag(name: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(flagKey(name));
    return raw === null ? fallback : raw === "1";
  } catch {
    return fallback;
  }
}
function writeFlag(name: string, on: boolean): void {
  try {
    localStorage.setItem(flagKey(name), on ? "1" : "0");
  } catch {
    // private windows may refuse, it just won't be remembered
  }
}

function renderSharedFold(people: Array<{ id: string; name: string; data: TaskEntry[] }>): void {
  const box = byId("tasks-shared");
  box.hidden = people.length === 0;
  if (!people.length) return;
  const total = people.reduce((sum, person) => sum + person.data.length, 0);
  const open = readFlag("shared-open", false);
  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "shared-toggle";
  toggle.setAttribute("aria-expanded", String(open));
  const label = document.createElement("span");
  label.textContent = t("Shared with me ({count})", { count: total });
  const caret = document.createElement("span");
  caret.className = "shared-caret";
  caret.textContent = open ? "▴" : "▾";
  toggle.append(label, caret);
  const body = document.createElement("div");
  body.className = "stack shared-body";
  body.hidden = !open;
  toggle.addEventListener("click", () => {
    const next = Boolean(body.hidden);
    body.hidden = !next;
    toggle.setAttribute("aria-expanded", String(next));
    caret.textContent = next ? "▴" : "▾";
    writeFlag("shared-open", next);
  });
  for (const person of people) {
    const group = document.createElement("div");
    group.className = "stack shared-box";
    const head = document.createElement("button");
    head.type = "button";
    head.className = "shared-group-head";
    const closed = !readFlag(`group-${person.id}`, true);
    head.setAttribute("aria-expanded", String(!closed));
    head.textContent = `${t("{name}'s {what} · read only", { name: person.name.toLowerCase(), what: t("tasks") })} (${person.data.length})`;
    const list = document.createElement("ul");
    list.className = "card-list";
    list.hidden = closed;
    renderList(list, person.data, (task) => buildTaskItem(task, true));
    const empty = document.createElement("p");
    empty.className = "empty";
    empty.textContent = t("nothing pending");
    empty.hidden = closed || person.data.length > 0;
    head.addEventListener("click", () => {
      const next = Boolean(list.hidden);
      list.hidden = !next;
      empty.hidden = !next || person.data.length > 0;
      head.setAttribute("aria-expanded", String(next));
      writeFlag(`group-${person.id}`, next);
    });
    group.append(head, list, empty);
    body.append(group);
  }
  box.replaceChildren(toggle, body);
}

// ---------- hidden plane tasks ----------

function renderHidden(): void {
  const hidden = visibleTasks(snapshot.hidden ?? []);
  const all = snapshot.hidden?.length ?? 0;
  byId("tasks-hidden-box").hidden = all === 0;
  const toggle = byId("tasks-hidden-toggle");
  toggle.textContent = showHidden ? t("hide the hidden") : t("show hidden ({count})", { count: all });
  toggle.setAttribute("aria-pressed", String(showHidden));
  const list = byId("tasks-hidden");
  list.hidden = !showHidden || all === 0;
  if (list.hidden) return;
  renderList(list, hidden, (task) => {
    const row = buildTaskItem(task, true);
    const restore = document.createElement("button");
    restore.type = "button";
    restore.className = "pill small task-restore";
    restore.textContent = t("restore");
    restore.addEventListener("click", (event) => {
      event.stopPropagation();
      void call<TaskSnapshot>(CHANNELS.tasksHide, taskKey(task), false).then(apply).catch((error) => flashToast(String(error).replace(/^.*Error: /, "")));
    });
    row.append(restore);
    return row;
  });
}

// ---------- board ----------

function buildTile(task: TaskEntry, status: TaskStatus): HTMLElement {
  const tile = document.createElement("li");
  tile.className = `task-tile from-${task.source} status-${status}`;
  tile.title = task.description;

  const top = document.createElement("div");
  top.className = "task-tile-top";
  const badge = document.createElement("span");
  badge.className = `badge ${task.source}`;
  badge.textContent = task.source;
  const title = document.createElement("span");
  title.className = "card-title";
  title.textContent = task.title;
  top.append(badge, title);

  const meta = document.createElement("span");
  meta.className = "card-meta";
  meta.textContent = [task.board, task.state, task.priority && t(task.priority), task.dueDate && t("due {date}", { date: formatDate(task.dueDate) })].filter(Boolean).join(" · ");

  const foot = document.createElement("div");
  foot.className = "task-tile-foot";
  const move = document.createElement("button");
  move.type = "button";
  move.className = "pill task-move";
  move.textContent = `${t("move")} ▾`;
  move.setAttribute("aria-label", t("move to"));
  move.addEventListener("click", (event) => {
    event.stopPropagation();
    openStatusPicker(task, status, (next) => void moveTask(task, next));
  });
  foot.append(move, buildTrashButton(task, () => tile));

  tile.append(top, meta, foot);
  if (task.url && task.source === "plane") tile.addEventListener("click", () => call(CHANNELS.openExternal, task.url));
  if (task.source === "chix") tile.addEventListener("click", () => openTaskEditor(task));
  return tile;
}

const kanban = (() => {
  let made: Kanban<Entry> | null = null;
  return () => made ??= createKanban<Entry>({
    host: byId("tasks-board"),
    columns: TASK_STATUSES.map((id) => ({ id, label: t(STATUS_LABEL[id]) })),
    build: (card) => {
      const status = card.column as TaskStatus;
      if (card.data.kind === "task") return buildTile(card.data.task, status);
      const job = card.data.job;
      const tile: HTMLElement = buildCommTile({
        job,
        sign: commSign(comms),
        badge: true,
        showStatus: true,
        open: () => openCommission(job.id),
        move: () => openStatusPicker({ title: job.name }, status, (next) => void moveComm(job, next)),
        trash: buildArmedTrash(t("delete commission"), true, () => void trashComm(job, tile))
      });
      tile.classList.add(`status-${status}`);
      return tile;
    },
    move: (card, to) => void (card.data.kind === "task" ? moveTask(card.data.task, to as TaskStatus) : moveComm(card.data.job, to as TaskStatus)),
    trash: (card, tile) => void (card.data.kind === "task" ? trashTask(card.data.task, tile) : trashComm(card.data.job, tile)),
    // deleting from the bot or the studio takes the same second tap as the button
    trashMode: (card) => (card.data.kind === "comm" || card.data.task.source === "chix" ? "arm" : "now")
  });
})();

function renderBoard(entries: Entry[]): void {
  kanban().paint(entries.map((entry) => ({ key: entryKey(entry), column: entry.kind === "task" ? statusOf(entry.task) : commTaskStatus(entry.job), data: entry })));
}

// moving a commission card changes the commission's own status, so the commissions board agrees. payments and pictures stay
async function moveComm(job: StudioJob, status: TaskStatus): Promise<void> {
  const next = TASK_TO_COMM[status];
  if (job.status === next) return;
  if (next === "done") justDone.add(job.id);
  comms = comms.map((item) => (item.id === job.id ? { ...item, status: next } : item));
  render();
  buzz(10);
  try {
    applyComms(await call<StudioState>(CHANNELS.studioAddJob, { ...job, status: next, imageData: [] }));
  } catch (error) {
    flashToast(String(error instanceof Error ? error.message : error).replace(/^.*Error: /, ""));
    await loadComms();
  }
}

async function trashComm(job: StudioJob, tile: HTMLElement): Promise<void> {
  await crunchCard(tile);
  try {
    applyComms(await call<StudioState>(CHANNELS.studioRemoveJob, job.id));
    buzz(12);
  } catch (error) {
    tile.classList.remove("task-crunch");
    flashToast(String(error instanceof Error ? error.message : error).replace(/^.*Error: /, ""));
  }
}

function applyComms(state: StudioState): void {
  if (!state?.jobs) return;
  comms = state.jobs;
  render();
}

async function loadComms(): Promise<void> {
  if (!canUse("studio", "edit")) {
    if (comms.length) {
      comms = [];
      render();
    }
    return;
  }
  await loadCommSign();
  applyComms(await call<StudioState>(CHANNELS.studioGet).catch(() => ({ jobs: comms }) as StudioState));
}

// a move shows at once and is saved after, the saved snapshot repaints the same thing
async function moveTask(task: TaskEntry, status: TaskStatus): Promise<void> {
  if (statusOf(task) === status) return;
  const key = taskKey(task);
  const next: Record<string, TaskStatus> = { ...(snapshot.meta?.status ?? {}) };
  if (status === "todo") delete next[key];
  else next[key] = status;
  snapshot = { ...snapshot, meta: { ...snapshot.meta, view: snapshot.meta?.view ?? mode, status: next } };
  render();
  buzz(10);
  if (!(await setStatus(task, status))) await load(true);
}

// ---------- loading ----------

function apply(fresh: TaskSnapshot): void {
  if (!fresh?.tasks) return;
  snapshot = fresh;
  setChixBoards(fresh.meta?.boards);
  if (fresh.meta && !modeTouched) mode = fresh.meta.view;
  if (fresh.meta?.pick && !pickTouched) pick = fresh.meta.pick;
  render();
}

let modeTouched = false;

async function loadShared(): Promise<void> {
  shared = await readSharedAll<TaskSnapshot>("tasks", { tasks: [], errors: [], updatedAt: "" });
  render();
}

async function load(force: boolean): Promise<void> {
  apply(await call<TaskSnapshot>(CHANNELS.tasksList, force));
}

async function refresh(): Promise<void> {
  const button = byId<HTMLButtonElement>("tasks-refresh");
  button.disabled = true;
  setPulsing(button, true);
  try {
    await load(true);
  } finally {
    button.disabled = false;
    setPulsing(button, false);
  }
}

function setMode(next: "list" | "board"): void {
  if (next === mode) return;
  mode = next;
  modeTouched = true;
  render();
  void call(CHANNELS.tasksView, next).catch(() => undefined);
}

export async function setupTasksView(): Promise<void> {
  byId("tasks-refresh").addEventListener("click", refresh);
  byId("tasks-add-chix").addEventListener("click", () => openTaskEditor(null, isChixBoard(currentPick()) ? currentPick() : CHIX_INBOX));
  byId("tasks-search").addEventListener("input", render);
  byId("tasks-source").addEventListener("change", render);
  document.querySelectorAll<HTMLElement>("#tasks-mode [data-mode]").forEach((button) => {
    button.addEventListener("click", () => setMode(button.dataset.mode === "board" ? "board" : "list"));
  });
  byId("tasks-hidden-toggle").addEventListener("click", () => {
    showHidden = !showHidden;
    render();
  });
  // a status or trash change comes back as a fresh snapshot, painted with the slide
  onTaskAction(apply);

  listen<TaskSnapshot>(CHANNELS.tasksUpdated, apply);
  listen<StudioState>(CHANNELS.studioUpdated, (state) => {
    if (canUse("studio", "edit")) applyComms(state);
  });
  onSharedChange("tasks", () => void loadShared());
  onViewShown("tasks", async () => {
    render();
    void loadShared();
    void loadComms();
    if (!snapshot.updatedAt) await load(false);
  });
}
